import React, { Dispatch, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import {
  BriefcaseBusiness,
  Building,
  HelpCircle,
  Home,
  MapPin,
  Plus,
  Upload,
  FolderOpen,
} from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import { DIALOG_TYPE } from '../../../../../constants';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import {
  BriefcaseBusinessIcon,
  FilePlusIcons,
  HomeIcons,
  LightningIcons,
  OfficeIcons,
} from '../../../../../icons/icons';
import { Button, Tooltip } from '../../../../../ui';
import AsyncCustomSelect from '../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../ui/custom_react_select/custom_select';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { createJobTitle } from '../../../../../utils/api_service/job_title';
import {
  createJobDescription,
  getJobDescription,
  getUniqueIdJobDescription,
} from '../../../../../utils/api_service/JobOpenings';
import useDebouncedJobTitleSearch from '../../../../../utils/helpers/AsyncSelect/JobTitleGet';
import { addJobTitleToRedux } from '../../../../../utils/redux_store/features/job_title_reducer';
import {
  DocumentConfiguration,
  DocumentRequirementStatus,
  IDocumentRequirement,
  JobDescription,
  JobReferenceNumberTypes,
  LocationType, showAllTypes, workType } from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import Collapsible from '../../ui/CollapsibleChildren';
import LoadingSpinner from '../../ui/LoaderSpinner';
import DropdownWithModal, { ModalOption } from '../../ui/SelectModalOption';
import { AIScoreWeightingModal, DEFAULT_DOCUMENTS, DocumentConfigurationModal } from './Components';
import { CreateTemplate, LoadJdTemplate } from './Containers';
import { IJobDescriptionTab } from './Interfaces/IJobDescription';
import { WORK_TYPES } from './Interfaces/IWorkTypes';
import ListFields from './ListFieldComponents';
import ImportJobDescription from './ModalContent';

const JobDescriptionTab = ({
  jobDescription,
  setJobDescription,
  jobTitle,
  setJobTitle,
  jobTitleList,
  completedElement,
  editable,
  setStages,
  setJobTitleLabel,
  setAutoSuitability,
}: IJobDescriptionTab) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(NS.adminPages);
  const JD = ADMIN_PAGES_KEYS.jobOpenings.jobDescription;
  const [showAll, setShowAll] = useState<showAllTypes>('showAll');
  const { loadOptionsDebounced } = useDebouncedJobTitleSearch();
  const [loadingJob, setLoadingJob] = useState<boolean>(() => {
    const loadingJob = ((jobDescription.filename &&
      jobDescription.filename !== '' &&
      !jobDescription.completed) ||
      (jobDescription.transcript &&
        jobDescription.transcript !== '' &&
        !jobDescription.completed)) as boolean;
    return loadingJob;
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);

  const [documentConfigurations, setDocumentConfigurations] = useState<DocumentConfiguration[]>(() => {
    // Initialize from jobDescription.documentRequirements
    if (jobDescription.documentRequirements) {
      return DEFAULT_DOCUMENTS.map(doc => {
        const req = jobDescription.documentRequirements?.find(
          r => r.label === doc.type || (doc.type === 'Resume / CV' && r.key === 'resume'),
        );
        if (req) {
          return {
            type: doc.type,
            requirement: req.isMandatory ? 'Mandatory' : 'Optional',
            allowMultiple: req.allowMultiple,
            description: req.description,
          };
        }
        return {
          type: doc.type,
          requirement: 'Disabled',
          allowMultiple: false,
        };
      });
    }
    return DEFAULT_DOCUMENTS.map(doc => ({
      type: doc.type,
      requirement: (doc as any).mandatoryFixed ? 'Mandatory' : 'Disabled',
      allowMultiple: false,
    }));
  });

  // Sync documentConfigurations when jobDescription.documentRequirements changes
  useEffect(() => {
    if (jobDescription.documentRequirements) {
      const updatedConfigs = DEFAULT_DOCUMENTS.map((doc) => {
        const req = jobDescription.documentRequirements?.find(
          (r) =>
            r.label === doc.type ||
            (doc.type === 'Resume / CV' && r.key === 'resume'),
        );
        if (req) {
          return {
            type: doc.type,
            requirement: req.isMandatory
              ? 'Mandatory'
              : 'Optional' as DocumentRequirementStatus,
            allowMultiple: req.allowMultiple,
            description: req.description,
          };
        }
        return {
          type: doc.type,
          requirement: 'Disabled' as DocumentRequirementStatus,
          allowMultiple: false,
        };
      });

      // Only update if the values are actually different to avoid infinite loops
      // Simple JSON check is sufficient here for these data structures
      if (
        JSON.stringify(updatedConfigs) !==
        JSON.stringify(documentConfigurations)
      ) {
        setDocumentConfigurations(updatedConfigs);
      }
    }
  }, [jobDescription.documentRequirements]);

  const options: ModalOption[] = [
    {
      value: 'importJobDescription',
      label: t(JD.importJobDescription),
      modalContent: (closeModal) => (
        <ImportJobDescription
          closeModal={closeModal}
          setLoading={setLoadingJob}
        />
      ),
      icon: (
        <Box
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: 'primary.light',
            color: 'primary.dark',
          }}
        >
          <Upload size={14} />
        </Box>
      ),
    },
    {
      value: 'loadJobTemplate',
      label: t(JD.loadJobTemplate),
      modalContent: (closeModal) => (
        <LoadJdTemplate
          closeModal={closeModal}
          jobDescription={jobDescription}
          setJobDescription={setJobDescription}
          setJobTitle={setJobTitle}
          onTemplateSelect={(template) => {
            toast.success(`Template "${template.templateTitle}" loaded successfully!`, {
              position: 'bottom-center',
            });
          }}
        />
      ),
      disabled: false,
      icon: (
        <Box
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: 'warning.light',
            color: 'warning.dark',
          }}
        >
          <FolderOpen size={14} />
        </Box>
      ),
    },
    {
      value: 'createJobTemplate',
      label: t(JD.createJobTemplate),
      modalContent: (closeModal) => (
        <CreateTemplate
          closeModal={closeModal}
          jobDescription={jobDescription}
          jobTitle={jobTitle}
          onTemplateSave={() => {
            toast.success('Template saved successfully!', {
              position: 'bottom-center',
            });
          }}
        />
      ),
      disabled: false,
      icon: (
        <Box
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: 'success.light',
            color: 'success.dark',
          }}
        >
          <Plus size={14} />
        </Box>
      ),
    },
  ];

  const defaultJobTitles = jobTitleList.map((jobTitle) => ({
    value: jobTitle.id,
    label: jobTitle.jobTitle,
  }));

  useEffect(() => {
    const fetchUniqueId = async () => {
      try {
        const response = await getUniqueIdJobDescription();
        const data = await response.json();
        setJobDescription((prevState) => ({
          ...prevState,
          jobReferenceNumberAuto: data.id,
        }));
      } catch (error) {
        toast.error('Error Fetching Unique Id', { position: 'bottom-center' });
      }
    };
    if (
      jobDescription.jobReferenceNumberAuto === null ||
      jobDescription.jobReferenceNumberAuto === ''
    ) {
      fetchUniqueId();
    }
  }, [jobDescription.jobReferenceNumberAuto]);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    let pollInterval: NodeJS.Timeout;
    if (loadingJob) {
      pollInterval = setInterval(() => {
        getJobDescription(jobDescription.jobOpeningId)
          .then((response) => {
            return response.json();
          })
          .then((data: JobDescription) => {
            // Keep jobTitle as the full object (either string or JobTitle object)
            // Don't convert to ID only - keep the full data structure
            setJobDescription({
              ...data,
              workType: data.workType ?? 'Permanent',
            });

            if (data.completed || data.status === 'Error') {
              setLoadingJob(false);
              clearInterval(pollInterval);
              if (data.status === 'Error') {
                toast.error('Error Processing Job Description, Retry Again', {
                  position: 'bottom-center',
                });
              } else {
                let jobTitleValue = data.jobTitle;
                if (jobTitleValue) {
                  const jobTitleOption: SelectOptions = {
                    label:
                      typeof jobTitleValue !== 'string'
                        ? jobTitleValue?.jobTitle
                        : jobTitleValue,
                    value:
                      typeof jobTitleValue !== 'string'
                        ? jobTitleValue?.id
                        : jobTitleValue,
                  };
                  setJobTitle(jobTitleOption);
                }
              }
            }
          })
          .catch(() => {
            toast.error('Error Fetching Job Description', {
              position: 'bottom-center',
            });
            setLoadingJob(false);
            clearInterval(pollInterval);
          });
      }, 3000);
    }
    return () => clearInterval(pollInterval);
  }, [loadingJob]);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  // Set initial location type based on jobLocation
  useEffect(() => {
    if (jobDescription.jobLocation) {
      if (jobDescription.jobLocation === 'Fully Remote') {
        // Only update if not already set to remote
        if (jobDescription.locationType !== 'remote') {
          setJobDescription((prev) => ({
            ...prev,
            locationType: 'remote',
          }));
        }
      } else if (
        !jobDescription.locationType ||
        jobDescription.locationType === 'remote'
      ) {
        // Parse and set location if locationType is not set OR if it's currently remote but jobLocation is not
        const loc = jobDescription.jobLocation.split(',').map((s) => s.trim());
        setJobDescription((prev) => ({
          ...prev,
          locationType: 'onsite',
          city: loc[0] || '',
          state: loc[1] || '',
          country: loc[2] || jobDescription.jobLocation,
        }));
      }
    }
  }, [jobDescription.jobLocation]);

  const defaultWeight = parseFloat((100 / 5).toFixed(2));
  const defaultAiScoreWeights = {
    location: defaultWeight,
    minimumWorkExperience: defaultWeight,
    mandatorySkills: defaultWeight,
    desirableSkills: defaultWeight,
    behavioralAttributes: defaultWeight,
  };
  const handleSave = (
    weights: {
      location: number;
      minimumWorkExperience: number;
      mandatorySkills: number;
      desirableSkills: number;
      behavioralAttributes: number;
    } | null,
  ) => {
    setJobDescription((prev) => ({
      ...prev,
      aiScoreWeights: weights || defaultAiScoreWeights,
    }));
    setModalOpen(false);
  };

  const toggleShowAll = () => {
    // Toggle between showAll and collapseAll
    const newShowAllState = showAll === 'showAll' ? 'collapseAll' : 'showAll';
    setShowAll(newShowAllState);
  };

  const handleChange = (selectedOption: SelectOptions) => {
    const workType: workType = selectedOption.value as workType;
    setJobDescription({ ...jobDescription, workType: workType });
  };

  const handleJobTitleChange = (selectedOptions: SelectOptions | null) => {
    if (selectedOptions === null) {
      setJobTitle(undefined);
      setJobDescription({ ...jobDescription, jobTitle: undefined });
      setJobTitleLabel('');
    } else {
      setJobTitle(selectedOptions);
      // Find the full JobTitle object from the list to store both id and label
      const fullJobTitle = jobTitleList.find(
        (jt) => jt.id === selectedOptions.value,
      );
      setJobDescription({
        ...jobDescription,
        jobTitle: fullJobTitle || selectedOptions.value,
      });
      setJobTitleLabel(selectedOptions.label);
    }
  };

  const handleCreateJobTitle = async (inputValue: string) => {
    try {
      const response = await createJobTitle(inputValue);
      if (response && response.id) {
        const newJobTitle: SelectOptions = {
          value: response.id,
          label: response.jobTitle,
        };
        // Add to Redux store
        dispatch(addJobTitleToRedux(newJobTitle));
        // Set as selected value
        setJobTitle(newJobTitle);
        // Store the full response object, not just the ID
        setJobDescription({ ...jobDescription, jobTitle: response });
        setJobTitleLabel(response.jobTitle);
        toast.success('Job Title created successfully', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Error creating Job Title', {
        position: 'bottom-center',
      });
    }
  };
  // Handle configure Documents
  const handleConfigureDocuments = () => {
    setIsDocModalOpen(true);
  };

  const handleDocumentRequirementChange = (
    type: string,
    requirement: DocumentRequirementStatus,
  ) => {
    // Resume / CV is always Mandatory
    const isLocked = DEFAULT_DOCUMENTS.find(
      (d) => d.type === type && (d as any).mandatoryFixed,
    );
    if (isLocked) return;

    setDocumentConfigurations((prev) => {
      const existingIndex = prev.findIndex((c) => c.type === type);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], requirement };
        return updated;
      }
      return [...prev, { type, requirement }];
    });
  };

  const handleAllowMultipleChange = (type: string, allowMultiple: boolean) => {
    setDocumentConfigurations((prev) => {
      const existingIndex = prev.findIndex((c) => c.type === type);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], allowMultiple };
        return updated;
      }
      return [...prev, { type, requirement: 'Optional', allowMultiple }];
    });
  };

  const makeAllMandatory = () => {
    setDocumentConfigurations(prev =>
      prev.map(c => ({ ...c, requirement: 'Mandatory' })),
    );
  };

  const makeAllOptional = () => {
    setDocumentConfigurations(prev =>
      prev.map(doc => ({
        ...doc,
        requirement: DEFAULT_DOCUMENTS.find(d => d.type === doc.type && (d as any).mandatoryFixed)
          ? 'Mandatory'
          : 'Optional',
      })),
    );
  };

  const makeAllDisabled = () => {
    setDocumentConfigurations(prev =>
      prev.map(doc => ({
        ...doc,
        requirement: DEFAULT_DOCUMENTS.find(d => d.type === doc.type && (d as any).mandatoryFixed)
          ? 'Mandatory'
          : 'Disabled',
      })),
    );
  };

  // Sync back to initial state when modal closes
  const handleDocModalClose = () => {
    const updatedRequirements: IDocumentRequirement[] = documentConfigurations
      .filter((config) => config.requirement !== 'Disabled')
      .map((config) => ({
        label: config.type,
        isMandatory: config.requirement === 'Mandatory',
        allowMultiple:
          config.type !== 'Resume / CV' && config.type !== 'Cover Letter'
            ? config.allowMultiple ?? false
            : false,
        acceptedFileTypes: ['.pdf', '.doc', '.docx'],
        description: config.description || '',
        key: config.type === 'Resume / CV' ? 'resume' : config.type,
      }));

    setJobDescription((prev) => ({
      ...prev,
      documentRequirements: updatedRequirements,
    }));
    setIsDocModalOpen(false);
  };
  const validateJobDescription = () => {
    const {
      jobTitle,
      jobReferenceNumber,
      jobLocation,
      workType,
      minimumWorkExperience,
    } = jobDescription;

    if (!jobTitle) {
      toast.error('Job Title is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (!jobReferenceNumber) {
      toast.error('Job Reference Number is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (!jobLocation) {
      toast.error('Job Location is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (!workType) {
      setJobDescription({ ...jobDescription, workType: 'Permanent' });
      toast.error('Work Type is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (minimumWorkExperience == null) {
      toast.error('Minimum Work Experience is a required field', {
        position: 'bottom-center',
      });
      return false;
    }
    return true;
  };

  const submitJobDescription = async () => {
    const fieldsValid = validateJobDescription();
    if (!fieldsValid) {
      return;
    }
    setLoading(true);
    try {
      // Always send aiScoreWeights, even if never opened
      const payload = {
        ...jobDescription,
        aiScoreWeights: jobDescription.aiScoreWeights || defaultAiScoreWeights,
      };
      const response = await createJobDescription(payload);
      if (response.ok) {
        const data: JobDescription = await response.json();
        setJobDescription({
          ...jobDescription,
          id: data.id,
        });

        // Sync Job Description fields to Auto-Suitability
        const skillsToAutoEvaluation = (skills: string[] | undefined) =>
          (skills || []).map((skill) => ({
            value: skill,
            relationToNext: 'OR' as const,
          }));

        setAutoSuitability((prev) => ({
          ...prev,
          jobLocation: jobDescription.jobLocation || prev.jobLocation,
          skills: skillsToAutoEvaluation(jobDescription.mandatorySkills),
          desirableSkills: skillsToAutoEvaluation(jobDescription.desirableSkills),
          educations: skillsToAutoEvaluation(jobDescription.educations),
          certifications: skillsToAutoEvaluation(jobDescription.certifications),
          behavioralAttributes: skillsToAutoEvaluation(jobDescription.behavioralAttributes),
          workExperience: {
            ...prev.workExperience,
            value: jobDescription.minimumWorkExperience || prev.workExperience.value,
          },
        }));

        setStages((prevStages: any[]) =>
          prevStages.map((stage) =>
            stage.label === 'Job Description'
              ? { ...stage, status: 'Completed' }
              : stage,
          ),
        );
        toast.success('Job Description Saved Successfully', {
          position: 'bottom-center',
        });
        completedElement();
      } else {
        toast.error('Error Submitting Job Description', {
          position: 'bottom-center',
        });
      }
    } catch {
      toast.error('Error Submitting Job Description', {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const changeJobReferenceType = (value: JobReferenceNumberTypes) => {
    if (value === 'Manual') {
      setJobDescription({
        ...jobDescription,
        jobReferenceNumber: '',
        jobReferenceType: value,
      });
    } else if (value === 'Auto') {
      console.log('test');
      setJobDescription({
        ...jobDescription,
        jobReferenceNumber: jobDescription.jobReferenceNumberAuto || '',
        jobReferenceType: value,
      });
    } else {
      setJobDescription({
        ...jobDescription,
        jobReferenceNumber: jobDescription.jobReferenceNumberJD || '',
        jobReferenceType: value,
      });
    }
  };

  return (
    <>
      { loadingJob ? (
        <LoadingSpinner label={jobDescription.status} />
      ) : (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minWidth: 0 }}>
          { !editable && (
            <Alert severity='warning' sx={{ py: 0.5, px: 2, borderRadius: 2 }}>
              <Typography variant='body2' sx={{ fontWeight: 500, color: 'warning.dark' }}>
                { t(JD.notEditableBanner) }{ ' ' }
                <Typography component='span' variant='body2' sx={{ color: 'warning.dark', opacity: 0.85 }}>
                  { t(JD.notEditableDetail) }
                </Typography>
              </Typography>
            </Alert>
          ) }
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent='space-between'
            alignItems={{ md: 'center' }}
            spacing={1.5}
            sx={{ mb: 0.5 }}
          >
            <ToggleButtonGroup
              value={showAll}
              exclusive
              onChange={(_, val) => val && setShowAll(val)}
              size='small'
              sx={{ bgcolor: 'background.paper', height: 38 }}
            >
              <ToggleButton
                value='showAll'
                sx={{
                  px: 1.75,
                  py: 0,
                  height: 38,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'success.main',
                    color: '#fff',
                    '&:hover': { bgcolor: 'success.dark' },
                  },
                }}
              >
                { t(JD.showAll) }
              </ToggleButton>
              <ToggleButton
                value='collapseAll'
                sx={{
                  px: 1.75,
                  py: 0,
                  height: 38,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'success.main',
                    color: '#fff',
                    '&:hover': { bgcolor: 'success.dark' },
                  },
                }}
              >
                { t(JD.collapseAll) }
              </ToggleButton>
            </ToggleButtonGroup>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'flex-end' }}
              spacing={1}
            >
              <Button
                onClick={handleConfigureDocuments}
                variant='primary'
                label={t(JD.configureDocuments)}
                hasIcon={true}
                icon={<FilePlusIcons size={16} />}
                disabled={!editable}
                sx={{ width: { xs: '100%', sm: 'auto' }, boxShadow: 1 }}
                dialogType={DIALOG_TYPE.INFO}
              />
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <MuiTooltip
                  title={
                    !editable && jobDescription.aiScoreWeights ? (
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant='caption' sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                          { t(JD.cvScoreWeightingsTitle) }
                        </Typography>
                        <Stack spacing={0.25}>
                          <Typography variant='caption' sx={{ display: 'block' }}>
                            { t(JD.weightingLocation) } { jobDescription.aiScoreWeights.location }%
                          </Typography>
                          <Typography variant='caption' sx={{ display: 'block' }}>
                            { t(JD.weightingMinWorkExp) }{ ' ' }
                            { jobDescription.aiScoreWeights.minimumWorkExperience }%
                          </Typography>
                          <Typography variant='caption' sx={{ display: 'block' }}>
                            { t(JD.weightingMandatorySkills) }{ ' ' }
                            { jobDescription.aiScoreWeights.mandatorySkills }%
                          </Typography>
                          <Typography variant='caption' sx={{ display: 'block' }}>
                            { t(JD.weightingDesirableSkills) }{ ' ' }
                            { jobDescription.aiScoreWeights.desirableSkills }%
                          </Typography>
                          <Typography variant='caption' sx={{ display: 'block' }}>
                            { t(JD.weightingBehavioural) }{ ' ' }
                            { jobDescription.aiScoreWeights.behavioralAttributes }%
                          </Typography>
                        </Stack>
                      </Box>
                    ) : ''
                  }
                  disableHoverListener={editable || !jobDescription.aiScoreWeights}
                  arrow
                >
                  <Box component='span'>
                    <Button
                      onClick={handleOpen}
                      variant='primary'
                      label={t(JD.cvScoreWeighting)}
                      hasIcon={true}
                      icon={<LightningIcons size={16} />}
                      disabled={!editable}
                      sx={{ width: { xs: '100%', sm: 'auto' }, boxShadow: 1 }}
                    />
                  </Box>
                </MuiTooltip>
              </Box>
              <DropdownWithModal
                options={options}
                disabled={!editable}
                label={t(JD.generateJobDescription)}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              />
            </Stack>
          </Stack>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Collapsible
              title={t(JD.detailsSectionTitle)}
              isOpen={showAll === 'showAll' || showAll === 'random'}
              toggleOpen={() => {
                if (showAll === 'random') {
                  setShowAll('collapseAll');
                } else {
                  toggleShowAll();
                }
              }}
            >
              <Box sx={{ p: 1 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Stack direction='row' alignItems='center' spacing={0.75} sx={{ mb: 0.5 }}>
                      <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.primary' }}>
                        { t(JD.jobTitleLabel) }
                      </Typography>
                      { jobTitle && (
                        <MuiTooltip title={jobTitle.label}>
                          <Box sx={{ p: 0.25, bgcolor: 'primary.50', borderRadius: 0.5, color: 'primary.main', display: 'flex', cursor: 'help' }}>
                            <HelpCircle size={14} />
                          </Box>
                        </MuiTooltip>
                      ) }
                    </Stack>
                    <AsyncCustomSelect
                      value={jobTitle}
                      defaultOptions={defaultJobTitles}
                      onChange={handleJobTitleChange}
                      loadOptions={(inputValue, callback) =>
                        loadOptionsDebounced(inputValue, callback)
                      }
                      isMulti={false}
                      disabled={!editable}
                      creatable={true}
                      onCreateOption={handleCreateJobTitle}
                    />
                  </Box>

                  <Box>
                    <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.primary' }}>
                      { t(JD.jobReferenceNumberLabel) }
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
                      <Box sx={{ flex: 1, width: '100%' }}>
                        <TextInput
                          name='title'
                          value={jobDescription.jobReferenceNumber || ''}
                          type='text'
                          onChange={(e) =>
                            setJobDescription({
                              ...jobDescription,
                              jobReferenceNumber: e.target.value,
                            })
                          }
                          disabled={
                            jobDescription.jobReferenceType === 'Auto' ||
                            jobDescription.jobReferenceType === 'JD' ||
                            !editable
                          }
                          placeholder='Enter reference number'
                        />
                      </Box>
                      <ToggleButtonGroup
                        value={jobDescription.jobReferenceType}
                        exclusive
                        onChange={(_, val) => val && changeJobReferenceType(val as JobReferenceNumberTypes)}
                        disabled={!editable}
                        size='small'
                        sx={{ bgcolor: 'grey.100', width: { xs: '100%', md: 'auto' } }}
                      >
                        { ['Auto', 'Manual', 'JD'].map((type) => (
                          <ToggleButton
                            key={type}
                            value={type}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              flex: { xs: 1, md: 'none' },
                              '&.Mui-selected': {
                                bgcolor: 'background.paper',
                                color: 'primary.main',
                                boxShadow: 1,
                              },
                            }}
                          >
                            { type }
                          </ToggleButton>
                        )) }
                      </ToggleButtonGroup>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.primary' }}>
                      { t(JD.workTypeLabel) }
                    </Typography>
                    <CustomSelect
                      name='jobRequisitionType'
                      value={{
                        label: jobDescription.workType
                          ? jobDescription.workType
                          : 'Permanent',
                        value: jobDescription.workType
                          ? jobDescription.workType
                          : 'Permanent',
                      }}
                      options={WORK_TYPES}
                      onChange={handleChange}
                      disabled={!editable}
                      creatable={true}
                      onCreateOption={handleCreateJobTitle}
                      placeholder='Search or create job title...'
                    />
                  </Box>

                  <Box>
                    <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.primary' }}>
                      { t(JD.minimumWorkExperienceLabel) }
                    </Typography>
                    <TextInput
                      name='minimumWorkExperience'
                      value={
                        jobDescription.minimumWorkExperience !== undefined &&
                        jobDescription.minimumWorkExperience !== null &&
                        !Number.isNaN(jobDescription.minimumWorkExperience)
                          ? String(jobDescription.minimumWorkExperience)
                          : ''
                      }
                      type='number'
                      onChange={(e) =>
                        setJobDescription({
                          ...jobDescription,
                          minimumWorkExperience:
                            e.target.value === ''
                              ? undefined
                              : parseInt(e.target.value),
                        })
                      }
                      disabled={!editable}
                    />
                  </Box>

                  { /* Right Column - Location */ }
                  <Box sx={{ gridColumn: { xs: '1', md: 'span 2', lg: 'span 2' } }}>
                    <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.primary' }}>
                      { t(JD.jobLocationLabel) }
                    </Typography>
                    <Stack spacing={1.5}>
                      <ToggleButtonGroup
                        value={jobDescription.locationType}
                        exclusive
                        onChange={(_, val) => {
                          if (!val) return;
                          const loc =
                            jobDescription.jobLocation
                              ?.split(',')
                              .map((s) => s.trim()) || [];
                          setJobDescription({
                            ...jobDescription,
                            locationType: val as LocationType,
                            ...(val === 'remote'
                              ? {
                                jobLocation: 'Fully Remote',
                                city: '',
                                state: '',
                                country: '',
                              }
                              : {
                                city: loc[0] || '',
                                state: loc[1] || '',
                                country:
                                  loc[2] ||
                                  jobDescription.jobLocation ||
                                  '',
                              }),
                          });
                        }}
                        disabled={!editable}
                        size='small'
                        sx={{
                          bgcolor: 'grey.100',
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                        }}
                      >
                        { [
                          {
                            type: 'remote',
                            label: t(JD.fullyRemote),
                            icon: <Home size={16} />,
                          },
                          {
                            type: 'hybrid',
                            label: t(JD.hybrid),
                            icon: <BriefcaseBusiness size={16} />,
                          },
                          {
                            type: 'onsite',
                            label: t(JD.fullyOnsite),
                            icon: <Building size={16} />,
                          },
                        ].map(({ type, label, icon }) => (
                          <ToggleButton
                            key={type}
                            value={type}
                            sx={{
                              px: 2,
                              py: 1,
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              '&.Mui-selected': {
                                bgcolor: 'background.paper',
                                color: 'primary.main',
                                boxShadow: 1,
                              },
                            }}
                          >
                            { icon }
                            <span>{ label }</span>
                          </ToggleButton>
                        )) }
                      </ToggleButtonGroup>

                      { !jobDescription.locationType && jobDescription.jobLocation && (
                        <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                          <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}>
                            { t(JD.currentLocation) }
                          </Typography>
                          <Stack direction='row' alignItems='center' spacing={1} sx={{ color: 'text.primary' }}>
                            <MapPin size={16} color='#64748b' />
                            <Typography variant='body2'>{ jobDescription.jobLocation }</Typography>
                          </Stack>
                        </Paper>
                      ) }

                      { (jobDescription.locationType === 'hybrid' || jobDescription.locationType === 'onsite') && (
                        <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                            { [
                              {
                                label: t(JD.city),
                                name: 'city' as const,
                                placeholder: t(JD.city),
                              },
                              {
                                label: t(JD.stateProvince),
                                name: 'state' as const,
                                placeholder: t(JD.stateProvince),
                              },
                              {
                                label: t(JD.country),
                                name: 'country' as const,
                                placeholder: t(JD.country),
                              },
                            ].map((field) => (
                              <Box key={field.name}>
                                <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                  { field.label }
                                </Typography>
                                <TextInput
                                  type='text'
                                  name={field.name}
                                  placeholder={field.placeholder}
                                  value={jobDescription[field.name] || ''}
                                  onChange={(e) => {
                                    const updates = {
                                      ...jobDescription,
                                      [field.name]: e.target.value,
                                    };
                                    const location = [
                                      updates.city,
                                      updates.state,
                                      updates.country,
                                    ]
                                      .filter(Boolean)
                                      .join(', ');

                                    setJobDescription({
                                      ...updates,
                                      jobLocation: location || e.target.value,
                                    });
                                  }}
                                  disabled={!editable}
                                />
                              </Box>
                            )) }
                          </Box>
                          { (jobDescription.city || jobDescription.state || jobDescription.country) && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                { t(JD.locationPreview) }
                              </Typography>
                              <Stack direction='row' alignItems='center' spacing={1} sx={{ color: 'text.primary' }}>
                                <MapPin size={16} color='#64748b' />
                                <Typography variant='body2'>{ jobDescription.jobLocation }</Typography>
                              </Stack>
                            </Box>
                          ) }
                        </Paper>
                      ) }
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Collapsible>
          </Box>

          <Box>
            <ListFields
              showAll={showAll}
              setShowAll={setShowAll}
              jobDescription={jobDescription}
              disabled={!editable}
              setJobDescription={setJobDescription}
              editable={editable}
            />
          </Box>
          <AIScoreWeightingModal
            isOpen={isModalOpen}
            onClose={handleClose}
            onSave={handleSave}
            aiScoreWeights={jobDescription.aiScoreWeights}
          />
          <DocumentConfigurationModal
            isOpen={isDocModalOpen}
            onClose={handleDocModalClose}
            editable={editable}
            documentConfigurations={documentConfigurations}
            onDocumentRequirementChange={handleDocumentRequirementChange}
            onAllowMultipleChange={handleAllowMultipleChange}
            onMakeAllMandatory={makeAllMandatory}
            onMakeAllOptional={makeAllOptional}
            onMakeAllDisabled={makeAllDisabled}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, mt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              onClick={() => submitJobDescription()}
              variant='primary'
              label={t(JD.saveJobDescription)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              disabled={
                loading ||
                !editable ||
                jobDescription.minimumWorkExperience === undefined ||
                jobDescription.jobTitle === '' ||
                jobDescription.jobLocation === '' ||
                jobDescription.jobReferenceNumber === '' ||
                (jobDescription.workType !== 'Permanent' &&
                  jobDescription.workType !== 'Part Time' &&
                  jobDescription.workType !== 'Casual' &&
                  jobDescription.workType !== 'Contract')
              }
            />
          </Box>
        </Box>
      ) }
    </>
  );
};

export default JobDescriptionTab;
